import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase'; // Ensure correct path
import { useUserData } from '../hooks/useUserData';

const TourContext = createContext();

export const useTour = () => useContext(TourContext);

export const TourProvider = ({ children }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, profile, isProfileComplete } = useUserData();

    const driverObj = useRef(null);
    const isNavigatingRef = useRef(false); // Lock for async navigation
    const isSwitchingRef = useRef(false); // Lock for tour switching
    const isPollingRef = useRef(false); // Lock to prevent double polling
    const [isTourActive, setIsTourActive] = useState(false);

    // Track location in ref to avoid stale closures in driver callbacks
    const locationRef = useRef(location);
    useEffect(() => {
        locationRef.current = location;
    }, [location]);

    // Clean up 'in_progress' tour status on page reload
    // Only 'completed' status should persist between sessions
    useEffect(() => {
        const tourStatus = localStorage.getItem('tour_status');
        if (tourStatus === 'in_progress') {
            localStorage.removeItem('tour_status');
        }
    }, []); // Empty dependency array = runs once on mount

    // Helper to update DB
    // const markTutorialComplete = async () => {
    //     // ALWAYS set local storage as fallback
    //     localStorage.setItem('tutorialCompleted', 'true');

    //     if (user?.uid) {
    //         try {
    //             await updateDoc(doc(db, 'users', user.uid), {
    //                 tutorialCompleted: true
    //             });
    //         } catch (error) {
    //             console.error("Error marking tutorial complete:", error);
    //             // We don't alert the user, as the local fallback handles the UX.
    //         }
    //     }
    // };

    // Helper to generic driver configuration
    const createDriver = (options = {}) => {
        const driverInstance = driver({
            showProgress: true,
            animate: true,
            allowClose: true,
            popoverClass: 'driverjs-theme',
            progressText: `{{current}} ${t('common.of') || 'de'} {{total}}`,
            doneBtnText: options.doneBtnText || t('tutorial.finish'),
            nextBtnText: t('tutorial.next') || t('common.next') || 'Siguiente',
            prevBtnText: t('tutorial.prev') || t('common.prev') || 'Anterior',
            // Explicitly handle close button click
            onCloseClick: () => {

                // Clean up tour_status immediately
                const currentStatus = localStorage.getItem('tour_status');
                if (currentStatus === 'in_progress') {
                    localStorage.removeItem('tour_status');
                }

                if (driverObj.current) {
                    driverObj.current.destroy();
                }
            },
            onDestroyStarted: () => {
                // Always reset navigation lock if a driver is destroyed
                isNavigatingRef.current = false;

                // If we are switching tours, don't perform full cleanup
                if (isSwitchingRef.current) {
                    return;
                }

                // Just cleanup internal state, don't mark as "User Finished whole thing" yet
                // unless explicitly handled.
                try {
                    setIsTourActive(false);
                    driverObj.current = null;

                    // Reset tour_status when user closes the tutorial
                    // This prevents it from reappearing
                    const currentStatus = localStorage.getItem('tour_status');
                    if (currentStatus === 'in_progress') {
                        localStorage.removeItem('tour_status');
                    }
                } catch (err) {
                    console.error("Driver cleanup error:", err);
                }
            }
        });

        // Helper to parse markdown
        const parseTutorialText = (text) => {
            if (!text || typeof text !== 'string') return text;
            return text
                .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>') // Bold
                .replace(/\n/g, '<br />'); // Newlines
        };

        // Monkey-patch setSteps to automatically parse markdown
        const originalSetSteps = driverInstance.setSteps;
        driverInstance.setSteps = (steps) => {
            const parsedSteps = steps.map(step => {
                if (!step.popover) return step;
                const newPopover = { ...step.popover };
                if (newPopover.title) newPopover.title = parseTutorialText(newPopover.title);
                if (newPopover.description) newPopover.description = parseTutorialText(newPopover.description);

                return {
                    ...step,
                    popover: newPopover
                };
            });
            originalSetSteps.call(driverInstance, parsedSteps);
        };

        return driverInstance;
    };

    const completeTutorial = async () => {
        if (user?.uid) {
            try {
                await updateDoc(doc(db, 'users', user.uid), {
                    tutorialCompleted: true,
                    tutorialCompletedAt: new Date().toISOString()
                });
            } catch (error) {
                console.error("Error saving tutorial completion:", error);
            }
        }
        localStorage.setItem('tour_status', 'completed');
        setIsTourActive(false);
    }

    // Phase 1: Profile (Auto-trigger if profile incomplete)
    useEffect(() => {
        const status = localStorage.getItem('tour_status');
        if (!user || isProfileComplete === null || profile?.tutorialCompleted || status === 'completed') return;

        // If profile is NOT complete, start Phase 1
        // (Existing logic preserved, just added status check)
        if (!isProfileComplete) {
            if (!isTourActive) {
                // Determine start step based on current route
                // const isInProfile = location.pathname === '/profile';
                // ... logic to start tour automatically? 
                // For now we rely on user clicking "Start Tour" in modal or we trigger it here?
                // The previous code had this logic but commented out mostly.
                // We'll leave it as is -> User triggers via Modal (OnboardingTutorial).
            }
        }
    }, [user, isProfileComplete, location.pathname, isTourActive, profile?.tutorialCompleted]);


    // Helper to handle navigation steps with polling
    const handleNavigationStep = (path, action = 'moveNext') => {
        if (isNavigatingRef.current) return; // Prevent double clicks/race conditions
        isNavigatingRef.current = true;

        navigate(path);

        const checkInterval = setInterval(() => {
            if (locationRef.current.pathname === path) {
                clearInterval(checkInterval);
                // Wait a tiny bit more for DOM paint
                setTimeout(() => {
                    if (driverObj.current && driverObj.current.isActive()) {
                        if (action === 'moveNext') driverObj.current.moveNext();
                        else if (action === 'movePrevious') driverObj.current.movePrevious();
                    }
                    isNavigatingRef.current = false;
                }, 500);
            }
        }, 100);

        // Safety timeout in case navigation fails or path is weird
        setTimeout(() => {
            clearInterval(checkInterval);
            if (isNavigatingRef.current) {
                // Force move
                if (driverObj.current && driverObj.current.isActive()) {
                    if (action === 'moveNext') driverObj.current.moveNext();
                    else if (action === 'movePrevious') driverObj.current.movePrevious();
                }
                isNavigatingRef.current = false;
            }
        }, 3000);
    };

    // Start Tour (Phase 1 & 2: Profile & Creation)
    const startTour = () => {
        if (driverObj.current) {
            driverObj.current.destroy();
        }

        // Mark as started
        localStorage.setItem('tour_status', 'in_progress');

        const newDriver = createDriver();
        driverObj.current = newDriver;
        setIsTourActive(true);

        const steps = [
            {
                element: '#nav-profile',
                popover: {
                    title: t('tutorial.step_profile_title'),
                    description: t('tutorial.step_profile_desc'),
                    side: "right",
                    align: 'center',
                    onNextClick: () => {
                        if (window.location.pathname !== '/profile') {
                            handleNavigationStep('/profile', 'moveNext');
                        } else {
                            newDriver.moveNext();
                        }
                    }
                }
            },
            {
                element: '#profile-form',
                popover: {
                    title: t('tutorial.step_fill_profile_title'),
                    description: t('tutorial.step_fill_profile_desc'),
                    side: "bottom",
                    align: 'center'
                }
            },
            {
                element: '#ipf-data-section',
                popover: {
                    title: t('tutorial.step_ipf_data_title') || 'Datos IPF',
                    description: t('tutorial.step_ipf_data_desc') || 'Completa tu género, peso corporal y tipo de equipamiento. Estos datos son opcionales pero útiles para calcular récords y comparar tu progreso.',
                    side: "left",
                    align: 'start'
                }
            },
            {
                element: '#profile-save-btn',
                popover: {
                    title: t('tutorial.step_save_profile_title'),
                    description: t('tutorial.step_save_profile_desc'),
                    side: "top",
                    align: 'center',
                    onNextClick: () => {
                        if (window.location.pathname !== '/') {
                            handleNavigationStep('/', 'moveNext');
                        } else {
                            newDriver.moveNext();
                        }
                    }
                }
            },
            {
                element: '#nav-home',
                popover: {
                    title: t('tutorial.step_routines_title'),
                    description: t('tutorial.step_routines_desc'),
                    side: "right",
                    align: 'center',
                    // Handle Back: Go to Profile
                    onPrevClick: () => {
                        if (window.location.pathname !== '/profile') {
                            handleNavigationStep('/profile', 'movePrevious');
                        } else {
                            newDriver.movePrevious();
                        }
                    },
                    // Handle Next: Ensure we are on home (redundant check but safe)
                    onNextClick: () => {
                        if (window.location.pathname !== '/') {
                            handleNavigationStep('/', 'moveNext');
                        } else {
                            newDriver.moveNext();
                        }
                    }
                }
            },
            {
                element: '#templates-btn',
                popover: {
                    title: t('tutorial.step_templates_title'),
                    description: t('tutorial.step_templates_desc'),
                    side: "bottom",
                    align: 'center',
                    onNextClick: () => {
                        // Abrir el modal de plantillas
                        const btn = document.querySelector('#templates-btn');
                        if (btn) {
                            btn.click();
                            // Esperar a que el modal se abra antes de continuar
                            setTimeout(() => {
                                newDriver.moveNext();
                            }, 300);
                        } else {
                            newDriver.moveNext();
                        }
                    }
                }
            },
            {
                element: '#template-filters',
                popover: {
                    title: t('tutorial.step_template_filters_title') || 'Filtros de Búsqueda',
                    description: t('tutorial.step_template_filters_desc') || 'Filtra por nivel (Principiante/Intermedio/Avanzado) y objetivo (Hipertrofia/Fuerza/Resistencia) para encontrar tu rutina ideal.',
                    side: "bottom",
                    align: 'start'
                }
            },
            {
                element: '#template-list',
                popover: {
                    title: t('tutorial.step_template_list_title') || 'Rutinas Disponibles',
                    description: t('tutorial.step_template_list_desc') || 'Aquí verás todas las plantillas. Haz clic en cualquiera para ver su contenido completo en el panel derecho.',
                    side: "right",
                    align: 'start'
                }
            },
            {
                element: '#template-preview',
                popover: {
                    title: t('tutorial.step_template_preview_title') || 'Vista Previa',
                    description: t('tutorial.step_template_preview_desc') || 'Aquí puedes ver los bloques, días y ejercicios completos de cada plantilla antes de usarla. ¡Explora y elige la que más te guste!',
                    side: "left",
                    align: 'start',
                    onNextClick: () => {
                        // Cerrar el modal antes de continuar usando el ID específico
                        const closeBtn = document.querySelector('#template-close-btn');
                        if (closeBtn) {
                            closeBtn.click();
                        }
                        setTimeout(() => {
                            newDriver.moveNext();
                        }, 200);
                    }
                }
            },
            {
                element: '#create-routine-btn',
                popover: {
                    title: t('tutorial.step_create_routine_title'),
                    description: t('tutorial.step_create_routine_desc'),
                    side: "bottom",
                    align: 'center',
                    nextBtnText: t('routines.new_routine') || "Crear Rutina",
                    // Auto-click the button when user clicks Next
                    onNextClick: () => {
                        const btn = document.querySelector('#create-routine-btn');
                        if (btn) {
                            isSwitchingRef.current = true;
                            // Use dispatchEvent to ensure React handles the click properly
                            btn.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
                            // Give a moment for the editor to mount before destroying
                            setTimeout(() => {
                                newDriver.destroy();
                            }, 100);
                        } else {
                            newDriver.destroy();
                        }
                    }
                }
            },

        ];

        newDriver.setSteps(steps);
        newDriver.drive();
    };

    // Phase 3: Editor Logic (Split into sub-phases)
    const proceedToEditorTour = () => {
        const status = localStorage.getItem('tour_status');
        if (status !== 'in_progress') return;
        if (isPollingRef.current) return;

        isPollingRef.current = true;

        // Wait for Editor to mount and input to be ready
        let attempts = 0;
        const checkInterval = setInterval(() => {
            attempts++;
            const editorInput = document.querySelector('#editor-name');

            // Allow up to 100 attempts (10 seconds)
            if (attempts > 100) {
                clearInterval(checkInterval);
                isPollingRef.current = false;
                return;
            }

            if (editorInput) {
                clearInterval(checkInterval);
                isPollingRef.current = false;

                // 1. Cleanup old driver FIRST (while isSwitching is still true)
                // This prevents onDestroyStarted from clearing state prematurely
                if (driverObj.current) {
                    try { driverObj.current.destroy(); } catch (e) { }
                    driverObj.current = null;
                }

                // 2. NOW reset switching flag (after manual cleanup)
                isSwitchingRef.current = false;

                // 3. Reset state
                setIsTourActive(false);

                if (localStorage.getItem('tour_status') === 'completed') return;

                // 4. Start new tour after delay to avoid race conditions
                setTimeout(() => {
                    const newDriver = createDriver({
                        doneBtnText: t('common.understood') || 'ENTENDIDO',
                        showButtons: ['next', 'previous']
                    });
                    driverObj.current = newDriver;
                    setIsTourActive(true);

                    // Wait for add-block button to exist before setting steps
                    const waitForButton = setInterval(() => {
                        const addBlockBtn = document.querySelector('#add-block-btn');
                        if (addBlockBtn) {
                            clearInterval(waitForButton);

                            newDriver.setSteps([
                                {
                                    element: '#editor-name',
                                    popover: {
                                        title: t('tutorial.step_editor_name_title'),
                                        description: t('tutorial.step_editor_name_desc'),
                                        side: "bottom",
                                        align: "start"
                                    }
                                },
                                {
                                    element: '#add-block-btn',
                                    popover: {
                                        title: t('tutorial.step_add_block_title'),
                                        description: t('tutorial.step_add_block_desc'),
                                        side: "top",
                                        align: "center",
                                        onNextClick: () => {
                                            // Securely destroy THIS driver instance
                                            try { newDriver.destroy(); } catch (e) { console.error(e); }
                                        },
                                        onPopoverRender: () => {
                                            const targetBtn = document.querySelector('#add-block-btn');
                                            if (targetBtn) {
                                                targetBtn.onclick = () => {
                                                    // Don't prevent default, let the app add the block
                                                    // But destroy the tour
                                                    try { newDriver.destroy(); } catch { }
                                                };
                                            }
                                        }
                                    }
                                }
                            ]);
                            // Reset switching flag to ensure cleanup works
                            isSwitchingRef.current = false;
                            newDriver.drive();
                        }
                    }, 100);
                }, 500);

            }
        }, 100);
    };

    const proceedToBlockCreationTour = () => {
        const status = localStorage.getItem('tour_status');
        if (status !== 'in_progress') return;
        if (isPollingRef.current) return;

        let attempts = 0;
        const checkInterval = setInterval(() => {
            attempts++;
            const weeksInput = document.querySelector('#block-weeks-container-0');

            if (attempts > 100) {
                clearInterval(checkInterval);
                return;
            }

            if (weeksInput) {
                clearInterval(checkInterval);

                if (driverObj.current) {
                    try { driverObj.current.destroy(); } catch { }
                    driverObj.current = null;
                }

                setIsTourActive(false);
                if (localStorage.getItem('tour_status') === 'completed') return;

                setTimeout(() => {
                    const newDriver = createDriver({
                        doneBtnText: t('common.understood'),
                        // Remove next button text for Enter Block step if possible, or make it clear
                    });
                    driverObj.current = newDriver;
                    setIsTourActive(true);

                    newDriver.setSteps([
                        {
                            element: '#block-weeks-container-0',
                            popover: {
                                title: t('tutorial.step_weeks_title'),
                                description: t('tutorial.step_weeks_desc'),
                                side: "bottom",
                                align: "center"
                            }
                        },
                        {
                            element: '#block-item-0',
                            popover: {
                                title: t('tutorial.step_enter_block_title'),
                                description: t('tutorial.step_enter_block_desc'),
                                side: "top",
                                align: "center",
                                showButtons: [], // Hide buttons if possible, or assume user clicks raw element
                                // If driver.js doesn't support hiding buttons easily per step via simple config, 
                                // we rely on the user clicking the element.
                            }
                        }
                    ]);
                    newDriver.drive();
                }, 100);
            }
        }, 100);
    };

    const proceedToBlockDetailTour = () => {
        const status = localStorage.getItem('tour_status');
        if (status !== 'in_progress') return;
        if (isPollingRef.current) return;

        let attempts = 0;
        const checkInterval = setInterval(() => {
            attempts++;
            const addDayBtn = document.querySelector('#add-day-btn');

            if (attempts > 100) {
                clearInterval(checkInterval);
                return;
            }

            if (addDayBtn) {
                clearInterval(checkInterval);

                if (driverObj.current) {
                    try { driverObj.current.destroy(); } catch { }
                    driverObj.current = null;
                }

                setIsTourActive(false);
                if (localStorage.getItem('tour_status') === 'completed') return;

                setTimeout(() => {
                    const newDriver = createDriver({ doneBtnText: t('common.understood') });
                    driverObj.current = newDriver;
                    setIsTourActive(true);

                    newDriver.setSteps([
                        {
                            element: '#add-day-btn',
                            popover: {
                                title: t('tutorial.step_add_day_title'),
                                description: t('tutorial.step_add_day_desc'),
                                side: "top",
                                align: "center",
                                onNextClick: () => {
                                    const btn = document.querySelector('#add-day-btn');
                                    if (btn) btn.click();
                                    newDriver.destroy();
                                }
                            }
                        }
                    ]);
                    newDriver.drive();
                }, 100);
            }
        }, 100);
    };

    const proceedToExerciseTour = () => {
        const status = localStorage.getItem('tour_status');
        if (status !== 'in_progress') return;
        if (isPollingRef.current) return;

        let attempts = 0;
        const checkInterval = setInterval(() => {
            attempts++;
            const addExBtn = document.querySelector('#add-exercise-btn');

            if (attempts > 100) {
                clearInterval(checkInterval);
                return;
            }

            if (addExBtn) {
                clearInterval(checkInterval);

                // Cleanup old driver
                if (driverObj.current) {
                    try { driverObj.current.destroy(); } catch { }
                    driverObj.current = null;
                }

                setIsTourActive(false);
                if (localStorage.getItem('tour_status') === 'completed') return;

                setTimeout(() => {
                    const newDriver = createDriver({ doneBtnText: t('common.understood') });
                    driverObj.current = newDriver;
                    setIsTourActive(true);

                    newDriver.setSteps([
                        {
                            element: '#add-exercise-btn',
                            popover: {
                                title: t('tutorial.step_add_exercise_title'),
                                description: t('tutorial.step_add_exercise_desc'),
                                side: "bottom",
                                align: "center",
                                onNextClick: () => {
                                    // Click the button automatically
                                    const btn = document.querySelector('#add-exercise-btn');
                                    if (btn) {
                                        btn.click();
                                    }
                                    newDriver.destroy();
                                }
                            }
                        }
                    ]);
                    newDriver.drive();
                }, 500);
            }
        }, 100);
    };

    // Deep dive into exercise card features
    const proceedToExerciseFeatures = () => {
        const status = localStorage.getItem('tour_status');
        if (status !== 'in_progress') return;
        if (isPollingRef.current) return;

        isPollingRef.current = true;

        let attempts = 0;
        const checkInterval = setInterval(() => {
            attempts++;
            // Try to find the first target section which indicates exercises are rendered
            const targetSection = document.querySelector('#exercise-target-section');

            if (attempts > 100) {
                clearInterval(checkInterval);
                isPollingRef.current = false;
                return;
            }

            if (targetSection) {
                clearInterval(checkInterval);
                isPollingRef.current = false;

                // Cleanup old driver
                if (driverObj.current) {
                    try { driverObj.current.destroy(); } catch { }
                    driverObj.current = null;
                }

                setIsTourActive(false);
                if (localStorage.getItem('tour_status') === 'completed') return;

                setTimeout(() => {
                    isSwitchingRef.current = false; // Ensure we are not in a switching state
                    const newDriver = createDriver({
                        doneBtnText: t('common.finish') || 'FINALIZAR',
                        nextBtnText: t('common.next') || 'SIGUIENTE',
                        prevBtnText: t('common.prev') || 'ANTERIOR'
                    });
                    driverObj.current = newDriver;
                    setIsTourActive(true);

                    // Determine best element for save button step
                    const saveBtnExists = document.querySelector('#save-changes-btn');

                    // Check if we are RESUMING from Wiki
                    const returningFromWiki = localStorage.getItem('wiki_visited') === 'true';

                    // If returning, start with empty steps (we will add the final ones later)
                    // Otherwise, start with the full intro set
                    let steps = returningFromWiki ? [] : [
                        // Save/Sync section step
                        {
                            element: saveBtnExists ? '#save-changes-btn' : '#save-sync-section',
                            popover: {
                                title: t('tutorial.step_save_title') || 'GUARDAR CAMBIOS',
                                description: saveBtnExists
                                    ? (t('tutorial.step_save_desc') || 'Los cambios se guardan primero en local (automáticamente) y luego se sincronizan con la nube.\n\nCuando veas el botón rojo pulsante, hay cambios pendientes de sincronizar.')
                                    : (t('tutorial.step_save_desc_missing') || 'Aquí verás el estado de sincronización. Los cambios se guardan automáticamente, pero si ves un botón rojo, púlsalo para sincronizar con la nube.'),
                                side: "bottom",
                                align: saveBtnExists ? "center" : "end"
                            }
                        },
                        // Export button step
                        {
                            element: '#export-routine-btn',
                            popover: {
                                title: t('tutorial.step_export_title') || 'EXPORTAR RUTINA',
                                description: t('tutorial.step_export_desc') || 'Descarga tu rutina en formato JSON. Útil para hacer backup o compartirla con otros usuarios de la app.',
                                side: "bottom",
                                align: "end"
                            }
                        },
                        // Date picker step
                        {
                            element: '#day-date-picker',
                            popover: {
                                title: t('tutorial.step_date_title') || 'FECHA DEL ENTRENAMIENTO',
                                description: t('tutorial.step_date_desc') || 'Modifica la fecha del día.\n\n📅 Si no eliges fecha, se autocompletará con la fecha del día en que modifiques los logs.\n\n🔄 Las semanas siguientes se rellenarán automáticamente sumando 7 días.',
                                side: "bottom",
                                align: "end"
                            }
                        },
                        {
                            element: '#exercise-target-section',
                            popover: {
                                title: t('tutorial.step_targets_title') || 'SERIES OBJETIVO',
                                description: t('tutorial.step_targets_desc') || 'Aquí defines lo que planeas hacer: peso, repeticiones, RPE...',
                                side: "top",
                                align: "center"
                            }
                        },
                        {
                            element: '#exercise-log-section',
                            popover: {
                                title: t('tutorial.step_logs_title') || 'SERIES REALIZADAS',
                                description: t('tutorial.step_logs_desc') || 'Aquí registras lo que realmente hiciste. ¡Es lo que cuenta para tu progreso!',
                                side: "top",
                                align: "center"
                            },
                            onHighlightStarted: () => {
                                // Scroll to a good position before showing this step
                                const element = document.querySelector('#exercise-log-section');
                                if (element) {
                                    element.scrollIntoView({ behavior: 'auto', block: 'center' });
                                }
                            }
                        },
                        {
                            element: '#rest-timer-select',
                            popover: {
                                title: t('tutorial.step_rest_timer_title') || 'TEMPORIZADOR',
                                description: t('tutorial.step_rest_timer_desc') || 'Configura el tiempo de descanso entre series.',
                                side: "left",
                                align: "center"
                            }
                        },
                        {
                            element: '#rest-timer-select',
                            popover: {
                                title: t('tutorial.step_timer_func_title') || 'CONTROL DEL CRONÓMETRO',
                                description: t('tutorial.step_timer_func_desc') || 'Al marcar una serie como completada, aparecerá un panel inferior donde podrás sumar tiempo (+30s), restar (-10s) o saltarlo si estás listo antes.',
                                side: "left",
                                align: "center"
                            }
                        },
                        {
                            element: '#complete-set-btn-0',
                            popover: {
                                title: t('tutorial.step_complete_set_title') || 'COMPLETAR SERIE',
                                description: t('tutorial.step_complete_set_desc') || 'Pulsa el círculo cuando termines una serie. Se guardará y activará AUTOMÁTICAMENTE el temporizador con el tiempo seleccionado.',
                                side: "top",
                                align: "center"
                            }
                        },
                        {
                            element: '#tour-target-headers',
                            popover: {
                                title: t('tutorial.step_columns_title') || 'VARIABLES DE ENTRENAMIENTO',
                                description: t('tutorial.step_columns_desc') || 'Aquí defines cada detalle de tu serie:\n\n• Peso (kg): La carga a levantar.\n• Reps: Número de repeticiones.\n• RPE: Esfuerzo percibido (1 fácil - 10 fallo).\n• Tempo: Velocidad de ejecución (ej. 3-0-1-0).\n• Tipo: Normal, Pesada, Drop Set, etc.',
                                side: "bottom",
                                align: "center"
                            },
                            onHighlightStarted: () => {
                                // Scroll back up to show the target header row which is in the objectives section
                                const element = document.querySelector('#tour-target-headers');
                                if (element) {
                                    element.scrollIntoView({ behavior: 'auto', block: 'center' });
                                }
                            }
                        },
                        {
                            element: '#plate-calc-btn',
                            popover: {
                                title: t('tutorial.step_plate_calc_title') || 'CALCULADORA DE DISCOS',
                                description: (t('tutorial.step_plate_calc_desc') || '¿No quieres sumar mentalmente? Usa esto para saber qué discos poner en la barra.') + '\n\n👆 **Pulsa el botón CALCULAR DISCOS para continuar**',
                                side: "top",
                                align: "center",
                                disableButtons: ['next'],
                                onPopoverRender: () => {
                                    const plateCalcBtn = document.querySelector('#plate-calc-btn');
                                    if (plateCalcBtn) {
                                        const handleClick = () => {
                                            plateCalcBtn.removeEventListener('click', handleClick);
                                            // Longer delay to let the modal open and render
                                            setTimeout(() => {
                                                if (driverObj.current) {
                                                    driverObj.current.moveNext();
                                                }
                                            }, 500);
                                        };
                                        plateCalcBtn.addEventListener('click', handleClick);
                                    }
                                }
                            }
                        },
                        // Step 1: Configure plates (settings button)
                        {
                            element: '#plate-calc-settings-btn',
                            popover: {
                                title: t('tutorial.step_plate_calc_settings_title') || 'CONFIGURAR DISCOS',
                                description: t('tutorial.step_plate_calc_settings_desc') || 'Pulsa este engranaje para seleccionar los discos que tienes disponibles en tu gimnasio. Así el cálculo será más preciso.',
                                side: "left",
                                align: "center",
                                onPopoverRender: () => {
                                    const settingsBtn = document.querySelector('#plate-calc-settings-btn');
                                    if (settingsBtn) {
                                        const handleClick = () => {
                                            settingsBtn.removeEventListener('click', handleClick);
                                            setTimeout(() => {
                                                if (driverObj.current) {
                                                    driverObj.current.moveNext();
                                                }
                                            }, 200);
                                        };
                                        settingsBtn.addEventListener('click', handleClick);
                                    }
                                }
                            }
                        },
                        // Step 2: Target weight input
                        {
                            element: '#plate-calc-weight-section',
                            popover: {
                                title: t('tutorial.step_plate_calc_weight_title') || 'PESO OBJETIVO',
                                description: t('tutorial.step_plate_calc_weight_desc') || 'Introduce el peso TOTAL que quieres levantar (barra + discos). Puedes usar los botones ±2.5kg o escribir directamente.',
                                side: "top",
                                align: "center"
                            }
                        },
                        // Step 3: Barbell type selector
                        {
                            element: '#plate-calc-barbell-section',
                            popover: {
                                title: t('tutorial.step_plate_calc_barbell_title') || 'TIPO DE BARRA',
                                description: t('tutorial.step_plate_calc_barbell_desc') || 'Selecciona el tipo de barra que usas. Cada una tiene un peso diferente que se resta del total para calcular los discos.',
                                side: "top",
                                align: "center"
                            }
                        },
                        // Step 4: Result explanation
                        {
                            element: '#plate-calc-result-section',
                            popover: {
                                title: t('tutorial.step_plate_calc_result_title') || 'RESULTADO',
                                description: t('tutorial.step_plate_calc_result_desc') || '**POR LADO** muestra los discos a poner EN CADA EXTREMO de la barra (se multiplican x2).\n\nEl resumen inferior muestra: peso de la barra + peso de discos = total.',
                                side: "top",
                                align: "center",
                                onPopoverRender: () => {
                                    // Listen for both "Siguiente" and "USAR PESO" button clicks
                                    const useWeightBtn = document.querySelector('#plate-calc-result-section button');
                                    if (useWeightBtn) {
                                        const handleUseWeight = () => {
                                            useWeightBtn.removeEventListener('click', handleUseWeight);
                                            setTimeout(() => {
                                                if (driverObj.current) {
                                                    driverObj.current.moveNext();
                                                }
                                            }, 300);
                                        };
                                        useWeightBtn.addEventListener('click', handleUseWeight);
                                    }
                                },
                                onNextClick: () => {
                                    // Close the plate calculator modal - find the X button in the modal header
                                    const modalCloseBtn = document.querySelector('.fixed.inset-0 button');
                                    if (modalCloseBtn) {
                                        modalCloseBtn.click();
                                    }
                                    setTimeout(() => {
                                        if (driverObj.current) {
                                            driverObj.current.moveNext();
                                        }
                                    }, 300);
                                }
                            }
                        },
                        {
                            element: '#add-target-set-btn',
                            popover: {
                                title: t('tutorial.step_add_set_title') || 'AÑADIR SERIE',
                                description: t('tutorial.step_add_set_desc') || 'Añade más series a este ejercicio si lo necesitas.',
                                side: "top",
                                align: "center"
                            }
                        },
                        {
                            element: '[id^="delete-target-set-"]',
                            popover: {
                                title: t('tutorial.step_delete_set_title') || 'BORRAR SERIE',
                                description: t('tutorial.step_delete_set_desc') || 'Elimina una serie específica si te has equivocado.',
                                side: "left",
                                align: "center"
                            }
                        },
                        {
                            element: '#btn-delete-exercise-0',
                            popover: {
                                title: t('tutorial.step_delete_exercise_title') || 'BORRAR EJERCICIO',
                                description: t('tutorial.step_delete_exercise_desc') || 'Si este ejercicio no te convence, elimínalo completamente aquí. Se abrirá un cuadro de confirmación.',
                                side: "bottom",
                                align: "end",
                                onPopoverRender: () => {
                                    // Prevent the delete button from actually working during tutorial
                                    const deleteBtn = document.querySelector('#btn-delete-exercise-0');
                                    if (deleteBtn) {
                                        const preventClick = (e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            // Just advance to next step instead
                                            if (driverObj.current) {
                                                driverObj.current.moveNext();
                                            }
                                        };
                                        deleteBtn.addEventListener('click', preventClick, { capture: true });
                                        // Store cleanup function
                                        deleteBtn._tourCleanup = () => {
                                            deleteBtn.removeEventListener('click', preventClick, { capture: true });
                                        };
                                    }
                                },
                                onDeselected: () => {
                                    // Cleanup when leaving this step
                                    const deleteBtn = document.querySelector('#btn-delete-exercise-0');
                                    if (deleteBtn && deleteBtn._tourCleanup) {
                                        deleteBtn._tourCleanup();
                                        delete deleteBtn._tourCleanup;
                                    }
                                }
                            }
                        }
                    ];

                    // Optional Steps (Check if elements exist)
                    const videoBtn = document.querySelector('#btn-video-exercise-0');
                    // Only show Wiki step if NOT returning from it
                    if (videoBtn && !returningFromWiki) {
                        steps.push({
                            element: '#btn-video-exercise-0',
                            popover: {
                                title: t('tutorial.step_video_title') || 'WIKI / VIDEO',
                                description: (t('tutorial.step_video_desc') || 'Pulsa este botón (libro o video) para ver la técnica correcta en la Wiki o un video demostrativo.') + '\n\n👆 **Pulsa el botón para ir a la Wiki**',
                                side: "bottom",
                                align: "end",
                                disableButtons: ['next'], // Force interactivity
                                onPopoverRender: () => {
                                    const btn = document.querySelector('#btn-video-exercise-0');
                                    if (btn) {
                                        const handleClick = () => {
                                            // We expect route change to happen, which triggers proceedToWikiTour
                                            // But we destroy this driver to be safe
                                            isSwitchingRef.current = true;
                                            if (driverObj.current) driverObj.current.destroy();
                                        };
                                        btn.addEventListener('click', handleClick, { once: true });
                                    }
                                }
                            }
                        });
                    }

                    // Complete Day - Opens Summary Modal
                    const completeDayBtn = document.querySelector('#complete-day-btn');
                    if (completeDayBtn) {
                        steps.push({
                            element: '#complete-day-btn',
                            popover: {
                                title: t('tutorial.step_complete_day_title') || 'COMPLETAR DÍA',
                                description: (t('tutorial.step_complete_day_desc_interactive') || 'Pulsa este botón para marcar el día como completado y ver el resumen.') + '\n\n👆 **Pulsa el botón para continuar**',
                                side: "bottom",
                                align: "center",
                                showButtons: ['previous', 'close'],
                                onPopoverRender: (popover) => {
                                    // Hide any next/done buttons that might appear
                                    const nextBtn = popover.nextButton;
                                    const doneBtn = popover.wrapper?.querySelector('.driver-popover-done-btn');
                                    if (nextBtn) nextBtn.style.display = 'none';
                                    if (doneBtn) doneBtn.style.display = 'none';

                                    const btn = document.querySelector('#complete-day-btn');
                                    if (btn) {
                                        const handleClick = () => {
                                            btn.removeEventListener('click', handleClick);
                                            // Close the tour immediately so the user can interact with the modal
                                            if (driverObj.current) {
                                                isSwitchingRef.current = true;
                                                driverObj.current.destroy();
                                            }
                                            // We DO NOT manually trigger the next step here. 
                                            // We wait for the 'Summary Modal' to actually open, 
                                            // which is detected by TrainingManager -> triggers proceedToPostWorkoutTour
                                        };
                                        btn.addEventListener('click', handleClick);
                                    }
                                }
                            }
                        });
                    } else {
                        // Fallback if complete-day button doesn't exist (day might already be complete)
                        steps.push({
                            element: '#day-view-weeks',
                            popover: {
                                title: t('tutorial.step_complete_day_title') || 'COMPLETAR DÍA',
                                description: t('tutorial.step_complete_day_desc') || 'Al finalizar todos los ejercicios, usa el botón de completar día (✓) o ver resumen (🏆) que aparecerá aquí arriba.\n\n¡Has terminado el tutorial!',
                                side: "bottom",
                                align: "center",
                                onNextClick: () => {
                                    if (driverObj.current) {
                                        driverObj.current.destroy();
                                    }
                                }
                            }
                        });
                    }
                    newDriver.setSteps(steps);

                    newDriver.drive();
                }, 500);
            }
        }, 100);
    };

    // Summary Modal Tour - Explains the workout summary after completing a day
    const proceedToSummaryModalTour = () => {
        const status = localStorage.getItem('tour_status');
        if (status !== 'in_progress') return;
        if (isPollingRef.current) return;

        // Cleanup old driver
        if (driverObj.current) {
            try { driverObj.current.destroy(); } catch { }
            driverObj.current = null;
        }

        setIsTourActive(true);

        // Use polling to wait for modal rendering (up to 15s)
        isPollingRef.current = true;
        let attempts = 0;
        const checkInterval = setInterval(() => {
            attempts++;
            const summaryModal = document.querySelector('#workout-summary-modal');

            if (attempts > 150) {
                clearInterval(checkInterval);
                isPollingRef.current = false;
                return;
            }

            if (summaryModal) {
                clearInterval(checkInterval);
                isPollingRef.current = false;
                const newDriver = createDriver({
                    doneBtnText: t('tutorial.finish') || 'FINALIZAR',
                    nextBtnText: t('common.next') || 'SIGUIENTE',
                    prevBtnText: t('common.prev') || 'ANTERIOR'
                });
                driverObj.current = newDriver;

                const steps = [
                    {
                        element: '#workout-summary-modal',
                        popover: {
                            title: t('tutorial.step_summary_modal_title') || 'RESUMEN DEL ENTRENAMIENTO',
                            description: t('tutorial.step_summary_modal_desc') || '¡Aquí está el resumen de tu sesión!\n\n• **100%** de completado\n• Series realizadas = objetivo\n• Duración total del entrenamiento\n• Desglose por ejercicio con mejores levantamientos',
                            side: "left",
                            align: "start"
                        }
                    },
                    {
                        element: '#visibility-toggle',
                        popover: {
                            title: t('tutorial.step_visibility_title') || 'VISIBILIDAD',
                            description: t('tutorial.step_visibility_desc') || 'Elige quién puede ver tu publicación:\n\n🌐 **Público**: Todos los usuarios\n👥 **Amigos**: Solo tus amigos',
                            side: "bottom",
                            align: "center"
                        }
                    },
                    {
                        element: '#image-upload-buttons',
                        popover: {
                            title: t('tutorial.step_photo_title') || 'AÑADIR FOTO',
                            description: t('tutorial.step_photo_desc') || 'Sube una foto de tu entrenamiento desde la galería o toma una con la cámara. ¡Motiva a los demás con tu progreso!',
                            side: "left",
                            align: "center"
                        }
                    },
                    {
                        element: '#summary-share-btn',
                        popover: {
                            title: t('tutorial.step_share_title') || 'COMPARTIR',
                            description: t('tutorial.step_share_desc') || 'Pulsa aquí para publicar tu entrenamiento en el feed y que tus amigos lo vean.',
                            side: "top",
                            align: "center",
                            onNextClick: () => {
                                // Close modal aggressively
                                const summaryModalEl = document.querySelector('#workout-summary-modal');
                                // Force close using Escape key simulation
                                document.dispatchEvent(new KeyboardEvent('keydown', { 'key': 'Escape' }));

                                if (summaryModalEl) {
                                    const closeBtn = summaryModalEl.querySelector('.sticky button');
                                    if (closeBtn) closeBtn.click();
                                }

                                setTimeout(() => {
                                    if (driverObj.current) {
                                        driverObj.current.moveNext();
                                    }
                                }, 400);
                            }
                        }
                    },
                    {
                        element: '#nav-feed',
                        popover: {
                            title: t('tutorial.step_feed_title') || 'COMUNIDAD',
                            description: t('tutorial.step_feed_desc') || 'Aquí podrás visualizar tanto tus publicaciones como las del resto de atletas. ¡Comparte tu progreso y motívate con otros!',
                            side: "right",
                            align: "center",
                            onPopoverRender: () => {
                                // Double check modal is closed when rendering this step
                                document.dispatchEvent(new KeyboardEvent('keydown', { 'key': 'Escape' }));
                                const summaryModalEl = document.querySelector('#workout-summary-modal');
                                if (summaryModalEl) {
                                    const closeBtn = summaryModalEl.querySelector('.sticky button');
                                    if (closeBtn) closeBtn.click();
                                }
                            }
                        }
                    }
                ];

                // Step 6: Celebration finale
                steps.push({
                    element: 'body',
                    popover: {
                        title: t('tutorial.step_celebration_title') || '🎉 ¡ENHORABUENA!',
                        description: t('tutorial.step_celebration_desc') || '¡Has completado el tutorial básico!\n\n✅ Crear y gestionar rutinas\n✅ Registrar tus entrenamientos\n✅ Compartir tu progreso\n\nPero esto es solo el principio... La app tiene **muchas más funciones** esperándote.\n\n🔍 ¡Explora y descúbrelas todas! 💪🔥',
                        side: "over",
                        align: "center",
                        onPopoverRender: () => {
                            // Add celebration confetti effect
                            launchConfetti();
                        },
                        onNextClick: () => {
                            if (driverObj.current) {
                                driverObj.current.destroy();
                            }
                            completeTutorial();
                        }
                    }
                });


                // Fallback logic: If a step's element is missing, point to the modal main container
                // This ensures all 6 steps are ALWAYS shown
                const validatedSteps = steps.map(step => {
                    if (typeof step.element === 'string' && step.element !== 'body') {
                        const el = document.querySelector(step.element);
                        // Check if element exists AND is visible (offsetParent implies visibility usually)
                        if (!el || el.offsetParent === null) {
                            console.warn(`[Tour] Element ${step.element} not found/hidden, falling back.`);

                            if (step.element === '#nav-feed') {
                                // If sidebar feed is missing (e.g. mobile or blocked), center on screen
                                step.element = 'body';
                                step.popover.side = 'over';
                                step.popover.align = 'center';
                            } else {
                                // Fallback for modal elements
                                const modalContent = document.querySelector('#workout-summary-modal > div');
                                step.element = modalContent ? '#workout-summary-modal > div' : '#workout-summary-modal';
                                // If even modal is gone (shouldn't happen for steps 1-4), fallback to body
                                if (!document.querySelector(step.element)) step.element = 'body';
                                step.popover.side = 'bottom';
                                step.popover.align = 'center';
                            }
                        }
                    }
                    return step;
                });

                newDriver.setSteps(validatedSteps);
                newDriver.drive();
            }
        }, 100);
    };

    // Confetti celebration effect
    const launchConfetti = () => {
        const duration = 3000;
        const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#a855f7'];
        const confettiCount = 150;

        const container = document.createElement('div');
        container.id = 'confetti-container';
        container.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:99999;overflow:hidden;';
        document.body.appendChild(container);

        for (let i = 0; i < confettiCount; i++) {
            const confetti = document.createElement('div');
            const size = Math.random() * 10 + 5;
            const color = colors[Math.floor(Math.random() * colors.length)];
            const left = Math.random() * 100;
            const delay = Math.random() * 1000;
            const rotation = Math.random() * 360;

            confetti.style.cssText = `
                position:absolute;
                width:${size}px;
                height:${size}px;
                background:${color};
                left:${left}%;
                top:-20px;
                border-radius:${Math.random() > 0.5 ? '50%' : '2px'};
                transform:rotate(${rotation}deg);
                animation:confettiFall ${2 + Math.random()}s ease-out ${delay}ms forwards;
            `;
            container.appendChild(confetti);
        }

        // Add keyframes if not exists
        if (!document.getElementById('confetti-keyframes')) {
            const style = document.createElement('style');
            style.id = 'confetti-keyframes';
            style.textContent = `
                @keyframes confettiFall {
                    0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                    100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
                }
            `;
            document.head.appendChild(style);
        }

        // Cleanup after animation
        setTimeout(() => {
            container.remove();
        }, duration + 2000);
    };

    const proceedToExerciseSelectorTour = () => {
        const status = localStorage.getItem('tour_status');
        if (status !== 'in_progress') return;

        // Cleanup old driver
        if (driverObj.current) {
            try { driverObj.current.destroy(); } catch { }
            driverObj.current = null;
        }

        setTimeout(() => {
            const originFilter = document.querySelector('#exercise-origin-filter');
            if (originFilter) {
                const newDriver = createDriver({ doneBtnText: t('common.understood') });
                driverObj.current = newDriver;
                setIsTourActive(true);

                newDriver.setSteps([
                    {
                        element: '#exercise-search-bar',
                        popover: {
                            title: t('tutorial.step_search_title'),
                            description: t('tutorial.step_search_desc'),
                            side: "bottom",
                            align: "center"
                        }
                    },
                    {
                        element: '#exercise-origin-filter',
                        popover: {
                            title: t('tutorial.step_origin_title'),
                            description: t('tutorial.step_origin_desc'),
                            side: "bottom",
                            align: "center"
                        }
                    },
                    {
                        element: '#exercise-muscle-filter',
                        popover: {
                            title: t('tutorial.step_muscle_title'),
                            description: t('tutorial.step_muscle_desc'),
                            side: "bottom",
                            align: "center"
                        }
                    },
                    {
                        element: '#exercise-list-container',
                        popover: {
                            title: t('tutorial.step_multiselect_title'),
                            description: t('tutorial.step_multiselect_desc'),
                            side: "top",
                            align: "center"
                        }
                    },
                    {
                        element: '#create-new-exercise-btn',
                        popover: {
                            title: t('tutorial.step_create_ex_title'),
                            description: t('tutorial.step_create_ex_desc'),
                            side: "top",
                            align: "center",
                            onNextClick: () => {
                                newDriver.destroy();
                                // We leave the user in the modal to select exercises
                            }
                        }
                    }
                ]);
                newDriver.drive();
            }
        }, 500);
    };

    // Phase 3: How to Use the App (Post-Creation)
    const proceedToPhase3 = () => {
        const status = localStorage.getItem('tour_status');
        if (status !== 'in_progress') return;

        // Cleanup old driver
        if (driverObj.current) {
            try { driverObj.current.destroy(); } catch { }
            driverObj.current = null;
        }

        setIsTourActive(false);

        setTimeout(() => {
            const newDriver = createDriver();
            driverObj.current = newDriver;
            setIsTourActive(true);

            newDriver.setSteps([
                {
                    element: '#nav-home',
                    popover: {
                        title: t('tutorial.step_phase3_home_title'),
                        description: t('tutorial.step_phase3_home_desc'),
                        side: "bottom",
                        align: "center",
                        onNextClick: () => {
                            if (window.location.pathname !== '/') {
                                handleNavigationStep('/', 'moveNext');
                            } else {
                                newDriver.moveNext();
                            }
                        }
                    }
                },
                {
                    element: '[data-routine-card]',
                    popover: {
                        title: t('tutorial.step_phase3_routine_card_title'),
                        description: t('tutorial.step_phase3_routine_card_desc'),
                        side: "top",
                        align: "center"
                    }
                },
                {
                    element: 'body',
                    popover: {
                        title: t('tutorial.tutorial_complete_title'),
                        description: t('tutorial.tutorial_complete_desc'),
                        side: "bottom",
                        align: "center",
                        onNextClick: () => {
                            newDriver.destroy();
                            completeTutorial();
                        }
                    }
                }
            ]);
            newDriver.drive();
        }, 500);
    };

    // REVISED STRATEGY: 
    // The tour breaks if elements disappear (list -> detail).
    // We will just let the user navigate. 
    // When they enter "Editor Mode" (Layout/TrainingManager mounts Editor), we trigger "Phase 2".

    // We can expose `proceedToEditorTour()` fn from context, and call it from `TrainingManager` when creating routine.

    const proceedToPostWorkoutTour = () => {
        proceedToSummaryModalTour();
    };

    const proceedToFeedTour = () => {
        if (!createDriver) return;

        // Prevent re-running if already done or not in tutorial mode
        const status = localStorage.getItem('tour_status');
        if (status !== 'in_progress') return;

        setTimeout(() => {
            const newDriver = createDriver();
            driverObj.current = newDriver;

            newDriver.setSteps([
                {
                    element: '#feed-post-item',
                    popover: {
                        title: t('tutorial.step_feed_post_title'),
                        description: t('tutorial.step_feed_post_desc'),
                        side: "bottom"
                    }
                },
                {
                    element: '#feed-delete-btn',
                    popover: {
                        title: t('tutorial.step_feed_delete_title'),
                        description: t('tutorial.step_feed_delete_desc'),
                        side: "left",
                        onNextClick: () => {
                            newDriver.destroy();
                            completeTutorial();
                        }
                    }
                }
            ]);
            newDriver.drive();
        }, 1000); // 1s delay for content load
    };

    // Wiki Tour - Helper to handle Wiki steps
    const proceedToWikiTour = () => {
        const status = localStorage.getItem('tour_status');
        if (status !== 'in_progress') return;

        // Cleanup old driver
        if (driverObj.current) {
            try { driverObj.current.destroy(); } catch { }
            driverObj.current = null;
        }

        setIsTourActive(true);

        // Wait for Wiki elements
        let attempts = 0;
        const checkInterval = setInterval(() => {
            attempts++;
            const videoSection = document.querySelector('#wiki-video-section') || document.querySelector('#wiki-instructions-section');

            if (attempts > 50) {
                clearInterval(checkInterval);
                return;
            }

            if (videoSection) {
                clearInterval(checkInterval);
                isSwitchingRef.current = false;

                const newDriver = createDriver({
                    doneBtnText: t('common.understood') || 'ENTENDIDO',
                    nextBtnText: t('common.next') || 'SIGUIENTE',
                    prevBtnText: t('common.prev') || 'ANTERIOR'
                });
                driverObj.current = newDriver;

                const steps = [];

                // 1. Video steps (if exists)
                if (document.querySelector('#wiki-video-section')) {
                    steps.push({
                        element: '#wiki-video-section',
                        popover: {
                            title: t('tutorial.step_wiki_video_title') || 'VIDEO EXPLICATIVO',
                            description: t('tutorial.step_wiki_video_desc') || 'Aquí encontrarás un video corto mostrando la técnica correcta del ejercicio.',
                            side: "bottom",
                            align: "center"
                        }
                    });
                }

                // 2. Instructions
                if (document.querySelector('#wiki-instructions-section')) {
                    steps.push({
                        element: '#wiki-instructions-section',
                        popover: {
                            title: t('tutorial.step_wiki_instr_title') || 'PASO A PASO',
                            description: t('tutorial.step_wiki_instr_desc') || 'Instrucciones detalladas y consejos clave para evitar lesiones y maximizar el estímulo.',
                            side: "top",
                            align: "center"
                        }
                    });
                }

                // 3. Back Button - Critical step to resume
                steps.push({
                    element: '#wiki-back-btn',
                    popover: {
                        title: t('tutorial.step_wiki_back_title') || 'VOLVER AL ENTRENAMIENTO',
                        description: t('tutorial.step_wiki_back_desc') || 'Pulsa aquí para regresar a tu rutina y continuar con el tutorial.',
                        side: "bottom",
                        align: "start",
                        disableButtons: ['next'], // Force them to click the button
                        onPopoverRender: () => {
                            const backBtn = document.querySelector('#wiki-back-btn');
                            if (backBtn) {
                                const handleBack = (e) => {
                                    // Stop propagation to ensure we handle it first
                                    e.preventDefault();
                                    e.stopPropagation();

                                    // Set flag so we know to resume when we get back
                                    localStorage.setItem('wiki_visited', 'true');

                                    isSwitchingRef.current = true;
                                    // Destroy this driver instance
                                    if (driverObj.current) driverObj.current.destroy();

                                    // Manually trigger navigation since we prevented default
                                    window.history.back();
                                };
                                backBtn.addEventListener('click', handleBack, { capture: true, once: true });
                            }
                        }
                    }
                });

                newDriver.setSteps(steps);
                newDriver.drive();
            }
        }, 100);
    };

    // Route Listener for Wiki (automatically start Wiki Tour if we land on /exercises/...)
    useEffect(() => {
        if (localStorage.getItem('tour_status') === 'in_progress') {
            if (location.pathname.startsWith('/exercises/')) {
                proceedToWikiTour();
            } else if (location.pathname === '/') {
                // If we are back at root (Day View) and just came from Wiki
                if (localStorage.getItem('wiki_visited') === 'true') {
                    // Resume the main tour
                    proceedToExerciseFeatures();
                }
            }
        }
    }, [location.pathname]);

    return (
        <TourContext.Provider value={{
            startTour,
            proceedToEditorTour,
            proceedToBlockCreationTour,
            proceedToBlockDetailTour,
            proceedToExerciseTour,
            proceedToExerciseSelectorTour,
            proceedToExerciseFeatures,
            proceedToPostWorkoutTour,
            proceedToSummaryModalTour,
            proceedToWikiTour, // NEW
            proceedToFeedTour,
            proceedToPhase3,
            completeTutorial,
            isTourActive,
            driverObj
        }}>
            {children}
        </TourContext.Provider>
    );
};
